// ─── agents.js — Phaser 3 sprite system ──────────────────────────────────────
// Replaces the old Three.js agents.js. Manages 5 pixel-art agents on a 2K
// background image using Phaser.GameObjects.Image with directional textures,
// state tints, movement tweens, speech bubbles, and idle bobbing.

// ─── Agent Config ─────────────────────────────────────────────────────────────
const AGENT_CONFIG = {
  atlas: { label: 'Atlas', role: 'Strategy & Coordination', color: '#ffd700' },
  muse:  { label: 'Muse',  role: 'Content Creation',        color: '#ff69b4' },
  pixel: { label: 'Pixel', role: 'Image Generation',        color: '#00bfff' },
  echo:  { label: 'Echo',  role: 'TikTok Publishing',       color: '#32cd32' },
  lens:  { label: 'Lens',  role: 'Analytics & Insights',    color: '#ff8c00' },
}

// ─── Desk Positions (fixed pixels at 1920×1080 internal resolution) ───────────
// Scale.FIT keeps these coordinates locked to the background regardless of
// browser window size. Calibrated from the isometric office background.
const DESK_POSITIONS = {
  atlas: { x: 739,  y: 524 },  // top-center desk, strategic overview
  lens:  { x: 1156, y: 544 },  // right desk, by windows
  echo:  { x: 1341, y: 676 },  // lower-right desk
  muse:  { x: 355,  y: 762 },  // lower-left desk
  pixel: { x: 746,  y: 986 },  // bottom desk, near door
}

// ─── Roundtable Positions (fixed pixels at 1920×1080) ─────────────────────────
const ROUNDTABLE_POSITIONS = {
  atlas: { x: 700, y: 580 },
  muse:  { x: 650, y: 640 },
  pixel: { x: 750, y: 700 },
  echo:  { x: 850, y: 640 },
  lens:  { x: 800, y: 580 },
}

// ─── 8-Direction Set ──────────────────────────────────────────────────────────
const DIRECTIONS = ['south', 'south-west', 'west', 'north-west', 'north', 'north-east', 'east', 'south-east']

// ─── Event → Visual State Map (named export) ──────────────────────────────────
export const EVENT_STATE_MAP = {
  workflow_run:              'working',
  workflow_completed:        'idle',
  content_brief_created:     'working',
  content_ready:             'celebrating',
  content_generation_failed: 'frustrated',
  content_posted:            'celebrating',
  posting_failed:            'frustrated',
  outside_posting_window:    'idle',
  daily_report:              'working',
  heartbeat_complete:        'idle',
  morning_roundtable:        'talking',
  fatal_error:               'frustrated',
  stats_computed:            'idle',
  stale_claim_reset:         'working',
}

// ─── Tint Colors per State ────────────────────────────────────────────────────
const STATE_TINTS = {
  idle:        null,        // clear tint
  working:     0x88ff88,   // green
  celebrating: 0xffd700,   // gold
  frustrated:  0xff4444,   // red
  talking:     0x88ddff,   // cyan
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Map an angle (radians, 0=east, PI/2=south) to the nearest of 8 directions.
 * @param {number} angle - Angle in radians
 * @returns {string} Direction name
 */
function angleToDirection(angle) {
  const normalized = ((angle * 180 / Math.PI) + 360) % 360
  const index = Math.round(normalized / 45) % 8
  // 0=east, 1=south-east, 2=south, 3=south-west, 4=west, 5=north-west, 6=north, 7=north-east
  const dirMap = ['east', 'south-east', 'south', 'south-west', 'west', 'north-west', 'north', 'north-east']
  return dirMap[index]
}

/**
 * Check whether a texture key exists in the Phaser texture manager.
 * @param {Phaser.Scene} scene
 * @param {string} key
 * @returns {boolean}
 */
function textureExists(scene, key) {
  try {
    return scene.textures.exists(key)
  } catch (_) {
    return false
  }
}

// ─── Factory ──────────────────────────────────────────────────────────────────

/**
 * Create and manage all 5 agents as Phaser sprites.
 *
 * @param {Phaser.Scene} scene - The active Phaser scene
 * @returns {object} Public API (see return statement at the bottom)
 */
export function createAgents(scene) {
  const agents = {}

  // ── Build each agent ──────────────────────────────────────────────────────
  for (const name of Object.keys(AGENT_CONFIG)) {
    const deskPos = DESK_POSITIONS[name]
    if (!deskPos) continue

    // Resolve texture
    const southKey = `${name}-south`
    let textureKey = southKey
    if (!textureExists(scene, southKey)) {
      textureKey = textureExists(scene, name) ? name : '__DEFAULT'
    }

    // Small shadow ellipse on the ground
    const shadow = scene.add.ellipse(deskPos.x, deskPos.y + 4, 40, 14, 0x000000, 0.35)
    shadow.setDepth(0)

    // Create sprite — origin at bottom-center (feet on ground)
    const sprite = scene.add.image(deskPos.x, deskPos.y, textureKey)
    sprite.setScale(2.5)
    sprite.setOrigin(0.5, 1.0)
    sprite.setInteractive()
    sprite.setData('agentName', name)
    sprite.setDepth(1)

    agents[name] = {
      sprite,
      shadow,
      state:       'idle',
      deskPos:     { ...deskPos },
      baseY:       deskPos.y,
      bubbleText:  null,
      bubbleBg:    null,
      bubbleTimer: null,
      idleTimer:   null,
      tween:       null,
    }
  }

  // ── Direction helpers ───────────────────────────────────────────────────────

  /**
   * Swap a sprite's texture to the named direction.
   * Falls back silently if the directional texture isn't loaded.
   *
   * @param {string} name - Agent key
   * @param {string} direction - One of DIRECTIONS
   */
  function setDirection(name, direction) {
    const agent = agents[name]
    if (!agent) return

    const key = `${name}-${direction}`
    if (textureExists(scene, key)) {
      agent.sprite.setTexture(key)
    }
    // If directional texture isn't loaded, keep current texture — no error thrown
  }

  // ── Movement ───────────────────────────────────────────────────────────────

  /**
   * Tween a sprite from its current position to (targetX, targetY).
   * Automatically rotates the sprite texture to face the direction of travel
   * during the tween, then resets to south on completion.
   *
   * @param {string} name - Agent key
   * @param {number} targetX
   * @param {number} targetY
   * @param {number} [duration=1500] - Tween duration in ms
   */
  function moveAgent(name, targetX, targetY, duration = 1500) {
    const agent = agents[name]
    if (!agent) return

    const { sprite } = agent

    // Cancel any in-progress tween
    if (agent.tween) {
      agent.tween.stop()
      agent.tween = null
    }

    // Calculate travel direction and set sprite facing
    const dx = targetX - sprite.x
    const dy = targetY - sprite.y
    const angle = Math.atan2(dy, dx)  // atan2: y positive = down (south)
    const direction = angleToDirection(angle)
    setDirection(name, direction)

    // Create tween — move both sprite and shadow
    const tween = scene.tweens.add({
      targets: sprite,
      x: targetX,
      y: targetY,
      duration,
      ease: 'Quad.easeInOut',
      onUpdate: () => {
        // Keep shadow under feet
        if (agent.shadow) {
          agent.shadow.setPosition(sprite.x, sprite.y + 4)
        }
        // Recalculate direction
        const cdx = targetX - sprite.x
        const cdy = targetY - sprite.y
        if (Math.abs(cdx) > 1 || Math.abs(cdy) > 1) {
          const cAngle = Math.atan2(cdy, cdx)
          setDirection(name, angleToDirection(cAngle))
        }
        _syncBubblePosition(name)
      },
      onComplete: () => {
        setDirection(name, 'south')
        agent.baseY = sprite.y
        agent.tween = null
        if (agent.shadow) agent.shadow.setPosition(sprite.x, sprite.y + 4)
        _syncBubblePosition(name)
      },
    })

    agent.tween = tween
  }

  /**
   * Teleport an agent back to its desk (no animation).
   * Convenience wrapper: use moveToStation(name) for animated return.
   */
  function moveToStation(name) {
    const agent = agents[name]
    if (!agent) return
    const { x, y } = agent.deskPos
    moveAgent(name, x, y)
  }

  /**
   * Move an agent to its roundtable position.
   *
   * @param {string} name - Agent key
   */
  function moveToRoundtable(name) {
    const pos = ROUNDTABLE_POSITIONS[name]
    if (!pos) return
    moveAgent(name, pos.x, pos.y)
  }

  /**
   * Move all agents to their roundtable positions simultaneously.
   */
  function moveAllToRoundtable() {
    for (const name of Object.keys(agents)) {
      moveToRoundtable(name)
    }
  }

  // ── State Management ────────────────────────────────────────────────────────

  /**
   * Set an agent's visual state: applies tint + animation, schedules idle reset.
   *
   * @param {string} name - Agent key
   * @param {string} state - 'idle' | 'working' | 'celebrating' | 'frustrated' | 'talking'
   */
  function setState(name, state) {
    const agent = agents[name]
    if (!agent) return

    agent.state = state

    const { sprite } = agent

    // Clear any pending auto-return timer
    if (agent.idleTimer) {
      agent.idleTimer.remove(false)
      agent.idleTimer = null
    }

    // Apply tint
    const tint = STATE_TINTS[state]
    if (tint == null) {
      sprite.clearTint()
    } else {
      sprite.setTint(tint)
    }

    // State-specific animations
    if (state === 'celebrating') {
      // Scale bounce: grow then return
      scene.tweens.add({
        targets: sprite,
        scaleX: { from: sprite.scaleX, to: sprite.scaleX * 1.3 },
        scaleY: { from: sprite.scaleY, to: sprite.scaleY * 1.3 },
        duration: 200,
        ease: 'Back.easeOut',
        yoyo: true,
        repeat: 1,
      })
    } else if (state === 'frustrated') {
      // Shake: rapid left-right oscillation
      const origX = sprite.x
      scene.tweens.add({
        targets: sprite,
        x: { from: origX - 3, to: origX + 3 },
        duration: 60,
        ease: 'Linear',
        yoyo: true,
        repeat: 5,
        onComplete: () => {
          sprite.x = origX
        },
      })
    }

    // Auto-return to idle after 30s (unless already idle)
    if (state !== 'idle') {
      agent.idleTimer = scene.time.delayedCall(30000, () => {
        if (agent.state === state) {
          setState(name, 'idle')
        }
        agent.idleTimer = null
      })
    }
  }

  // ── Event Processing ────────────────────────────────────────────────────────

  /**
   * Drive agent visual state and movement based on an event type string.
   * Uses EVENT_STATE_MAP to resolve the state, then applies movement side-effects.
   *
   * @param {string} agentName - Agent key
   * @param {string} eventType - Event type string from agent_events table
   */
  function processEvent(agentName, eventType) {
    const state = EVENT_STATE_MAP[eventType] || 'idle'
    setState(agentName, state)

    // Movement side-effects
    switch (eventType) {
      case 'morning_roundtable':
        moveAllToRoundtable()
        break

      case 'workflow_run':
      case 'heartbeat_complete':
        moveToStation(agentName)
        break

      case 'content_brief_created': {
        // Muse walks toward Pixel — midpoint between their desks
        const musePos  = DESK_POSITIONS['muse']
        const pixelPos = DESK_POSITIONS['pixel']
        if (musePos && pixelPos) {
          const midX = (musePos.x + pixelPos.x) / 2
          const midY = (musePos.y + pixelPos.y) / 2
          moveAgent('muse', midX, midY)
        }
        break
      }

      case 'content_ready': {
        // Pixel walks toward Echo — midpoint between their desks
        const pixelPos = DESK_POSITIONS['pixel']
        const echoPos  = DESK_POSITIONS['echo']
        if (pixelPos && echoPos) {
          const midX = (pixelPos.x + echoPos.x) / 2
          const midY = (pixelPos.y + echoPos.y) / 2
          moveAgent('pixel', midX, midY)
        }
        break
      }

      default:
        break
    }
  }

  // ── Speech Bubbles ──────────────────────────────────────────────────────────

  /**
   * Sync bubble position to sit above the sprite.
   * @param {string} name
   */
  function _syncBubblePosition(name) {
    const agent = agents[name]
    if (!agent || !agent.bubbleText) return

    const { sprite, bubbleText, bubbleBg } = agent
    const bx = sprite.x
    const by = sprite.y - sprite.displayHeight - 30

    bubbleText.setPosition(bx, by)

    if (bubbleBg) {
      const pad = 8
      bubbleBg.setPosition(
        bx - bubbleText.width / 2 - pad,
        by - bubbleText.height / 2 - pad
      )
      bubbleBg.setSize(bubbleText.width + pad * 2, bubbleText.height + pad * 2)
    }
  }

  /**
   * Show a speech bubble above the named agent.
   * The bubble auto-fades after 8 seconds. Any existing bubble is destroyed first.
   *
   * @param {string} name - Agent key
   * @param {string} text - Message to display (displayed as-is, max ~180px wide)
   */
  function showSpeechBubble(name, text) {
    const agent = agents[name]
    if (!agent) return

    const { sprite } = agent

    // Destroy old bubble if it exists
    if (agent.bubbleText) {
      agent.bubbleText.destroy()
      agent.bubbleText = null
    }
    if (agent.bubbleBg) {
      agent.bubbleBg.destroy()
      agent.bubbleBg = null
    }
    if (agent.bubbleTimer) {
      agent.bubbleTimer.remove(false)
      agent.bubbleTimer = null
    }

    const bx = sprite.x
    const by = sprite.y - sprite.displayHeight - 30

    // Create text object
    const bubbleText = scene.add.text(bx, by, text, {
      fontSize: '13px',
      fontFamily: 'monospace',
      color: '#e0e0e0',
      wordWrap: { width: 180 },
      align: 'center',
    })
    bubbleText.setOrigin(0.5, 0.5)
    bubbleText.setDepth(100)
    bubbleText.setAlpha(1)

    // Create dark background rectangle behind the text
    const pad = 8
    const bubbleBg = scene.add.rectangle(
      bx - bubbleText.width / 2 - pad,
      by - bubbleText.height / 2 - pad,
      bubbleText.width + pad * 2,
      bubbleText.height + pad * 2,
      0x0a0a1e,
      0.90
    )
    bubbleBg.setOrigin(0, 0)
    bubbleBg.setDepth(99)
    bubbleBg.setAlpha(1)
    bubbleBg.setStrokeStyle(1, 0x00ffcc, 0.8)

    agent.bubbleText = bubbleText
    agent.bubbleBg   = bubbleBg

    // Auto-fade both after 8 seconds
    agent.bubbleTimer = scene.time.delayedCall(7000, () => {
      scene.tweens.add({
        targets: [bubbleText, bubbleBg],
        alpha: 0,
        duration: 1000,
        ease: 'Linear',
        onComplete: () => {
          if (agent.bubbleText === bubbleText) {
            bubbleText.destroy()
            bubbleBg.destroy()
            agent.bubbleText  = null
            agent.bubbleBg    = null
            agent.bubbleTimer = null
          }
        },
      })
    })
  }

  // ── Per-Frame Update (idle bob) ─────────────────────────────────────────────
  // Hook into the scene's update via game events so main.js can stay clean.

  scene.game.events.on('scene-update', (time, delta) => {
    for (const [name, agent] of Object.entries(agents)) {
      const { sprite, shadow, tween, baseY } = agent

      // Only bob when no movement tween is active
      if (!tween || tween.isDestroyed()) {
        const offset = name.charCodeAt(0)
        const bob = Math.sin(time * 0.003 + offset) * 2
        sprite.y = baseY + bob
        if (shadow) shadow.setPosition(sprite.x, baseY + 4)
      }
    }
  })

  // ─── Return Public API ──────────────────────────────────────────────────────
  return {
    agents,
    AGENT_CONFIG,
    moveAgent,
    moveToStation,
    moveToRoundtable: (name) => {
      const pos = ROUNDTABLE_POSITIONS[name]
      if (!pos) return
      moveAgent(name, pos.x, pos.y)
    },
    moveAllToRoundtable,
    setState,
    processEvent,
    showSpeechBubble,
    setDirection,
  }
}
